import environment from '@/helpers/Environment'

// @see https://www.iban.com/country-codes
type CountryISO3 = string

// TODO Enable for more countries.
export const supportedCountries = new Set<CountryISO3>(['USA', 'GBR', 'CAN', 'AUS', 'NZL'])

export const addressFieldNamesWithState = ['address', 'city', 'state', 'zip_code', 'country']
export const addressFieldNamesWithoutState = ['address', 'city', 'zip_code', 'country']

export type LookupFunction = (input: string, country: CountryISO3) => Promise<any>

// TODO Remove once we no longer need to reproduce CookieBot bug.
export function getAddressAutocompleteApiKey(): string | undefined {
  const queryParam = window.tixAppState.queryParams?.smarty_api_key
  if (queryParam) {
    return queryParam
  } else {
    return environment.web.address_autocomplete?.api_key
  }
}

export function createAddressLookup(apiKey: string, country: CountryISO3): Promise<LookupFunction | null> {
  if (!supportedCountries.has(country)) {
    return Promise.resolve(null)
  }

  return import('smartystreets-javascript-sdk').then((smarty) => {
    const client = getAutocompleteClient(smarty.core, apiKey, country)

    return (input: string, country: CountryISO3) => {
      const lookup = createLookup(input, country, smarty)
      return client.send(lookup)
    }
  })
}

export function alterAddressFieldsForAutocomplete(fields: FormInputField[]): FormInputField[] {
  // Remove all address fields, other than the country and address.
  const otherAddressFields = new Set(['city', 'state', 'zip_code'])
  const result = fields.filter((field) => !otherAddressFields.has(field.key))

  // Find the position of the address field and get it.
  const address = result.findIndex((field) => field.key === 'address')
  const [addressField] = result.splice(address, 1)

  // Find the position of the country field.
  // This must be done after the splice above as that can change the position of the country field.
  const country = result.findIndex((field) => field.key === 'country')

  // Position the address field immediately after the country field.
  result.splice(country + 1, 0, addressField)

  return result
}

function getAutocompleteClient(smartyCore, apiKey: string, country: CountryISO3) {
  const credentials = new smartyCore.SharedCredentials(apiKey)

  if (country === 'USA') {
    const clientBuilder = new smartyCore.ClientBuilder(credentials).withLicenses(['us-autocomplete-pro-cloud'])
    return clientBuilder.buildUsAutocompleteProClient()
  } else {
    const clientBuilder = new smartyCore.ClientBuilder(credentials).withLicenses(['international-autocomplete-cloud'])
    return clientBuilder.buildInternationalAddressAutocompleteClient()
  }
}

function createLookup(input: string, country: string, { internationalAddressAutocomplete, usAutocompletePro }) {
  const lookup =
    country === 'USA'
      ? new usAutocompletePro.Lookup(input, country)
      : new internationalAddressAutocomplete.Lookup(input, country)

  lookup.maxResults = 10
  return lookup
}
